import { Box, Divider, Flex, Stack, Text } from '@mantine/core';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { useState } from 'react';
import { Client } from 'src/admin/users/types';
import { SelectedClientPill } from 'src/design-system/SelectedClientPill';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { formatName, NameFormat } from 'src/utils/nameFormatting';
import { matchesClientSearch } from 'src/utils/clientSearch';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { useClients } from '../hooks/useClients';
import { isMemberSelected } from './dataHelpers';

const CONDENSED_THRESHOLD = 3;

type Props = {
  selectedMembers: Client[];
  onToggle: (client: Client) => void;
  searchTerm?: string;
  noScroll?: boolean;
  excludeInFamilies?: boolean;
  radius?: number;
};

export default function MemberSearchList({
  selectedMembers,
  onToggle,
  searchTerm,
  noScroll,
  excludeInFamilies,
  radius = 12,
}: Props) {
  const { clients } = useClients();
  const { flagEnabledClientSearchFullName } = useFlags();
  const [isExpanded, setIsExpanded] = useState(false);

  const search = searchTerm?.trim() ?? '';

  const filtered = clients.filter(client => {
    if (excludeInFamilies && client.familyId) return false;
    if (isMemberSelected(client, selectedMembers)) return false;
    return matchesClientSearch(
      [client.firstName, client.lastName, client.emailAddress, client.personalDetails?.address],
      search,
      flagEnabledClientSearchFullName,
    );
  });

  let emptyMessage = 'No clients';
  if (excludeInFamilies) emptyMessage = 'No clients without families';
  if (search) emptyMessage = 'No matching clients';

  const isCondensed = selectedMembers.length > CONDENSED_THRESHOLD && !isExpanded;

  const condensedLabel = isCondensed
    ? (() => {
        const first = formatName(
          selectedMembers[0].firstName,
          selectedMembers[0].lastName,
          NameFormat.NATURAL,
        );
        const second = formatName(
          selectedMembers[1].firstName,
          selectedMembers[1].lastName,
          NameFormat.NATURAL,
        );
        const remaining = selectedMembers.length - 2;
        return `${first}, ${second} and ${remaining} more`;
      })()
    : null;

  return (
    <Box
      p="sm"
      bd="var(--solid-border)"
      style={{
        borderRadius: radius,
        height: noScroll ? undefined : '100%',
        overflowY: noScroll ? undefined : 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--mantine-spacing-xxs)',
      }}
    >
      {selectedMembers.length > 0 && (
        <>
          <Flex wrap="wrap" gap="xxs" pb="sm" align="center">
            {isCondensed ? (
              <Flex
                align="center"
                gap="xs"
                px="md"
                py="xs"
                bg="gray.0"
                style={{ borderRadius: 20, cursor: 'pointer' }}
                onClick={() => setIsExpanded(true)}
              >
                <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.10">
                  {condensedLabel}
                </Text>
                <IconChevronDown size={16} />
              </Flex>
            ) : (
              <>
                {selectedMembers.map(client => (
                  <SelectedClientPill
                    key={client.id}
                    label={formatName(client.firstName, client.lastName, NameFormat.NATURAL)}
                    tooltip={client.emailAddress ?? undefined}
                    onClick={() => onToggle(client)}
                  />
                ))}
                {selectedMembers.length > CONDENSED_THRESHOLD && (
                  <Flex
                    align="center"
                    gap="xxs"
                    px="sm"
                    py="xs"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setIsExpanded(false)}
                  >
                    <Text size="sm" c="gray.7">
                      Show less
                    </Text>
                    <IconChevronUp size={16} />
                  </Flex>
                )}
              </>
            )}
          </Flex>
          <Divider mx="sm" color="gray.0" size={1} />
        </>
      )}
      {filtered.length === 0 && (
        <Text size="sm" c="gray.5" fw={FONT_WEIGHTS.medium} ta="center" py="md">
          {emptyMessage}
        </Text>
      )}
      {filtered.map(client => {
        const email = client.emailAddress ?? '';
        const address = client.personalDetails?.address ?? '';
        const subtext = [email, address].filter(Boolean).join(' • ');

        return (
          <Flex
            key={client.id}
            justify="space-between"
            align="center"
            gap="sm"
            my="xxs"
            mx="md"
            style={{ cursor: 'pointer' }}
            onClick={() => onToggle(client)}
          >
            <Stack gap="xxxs">
              <Text fw={FONT_WEIGHTS.semibold} size="sm" c="gray.10">
                {formatName(client.firstName, client.lastName, NameFormat.NATURAL)}
              </Text>
              {subtext && (
                <Text size="xs" fw={FONT_WEIGHTS.medium} c="gray.7">
                  {subtext}
                </Text>
              )}
            </Stack>
            <Text size="sm" fw={FONT_WEIGHTS.semibold} c="brand.6" style={{ flexShrink: 0 }}>
              Select client
            </Text>
          </Flex>
        );
      })}
    </Box>
  );
}
